import React, { useState, useEffect } from 'react';
import { QRCode } from 'react-qr-code';
import { FaQrcode, FaCopy, FaCheck } from 'react-icons/fa';
import { createShareableUrl } from '../utils/tokenUtils';
import { fanPath } from '../config/fanMembersConfig';

const MemberCardQR = ({ phoneNumber, activityType, activityId, fanMember, theme = 'light' }) => {
    const [shareableUrl, setShareableUrl] = useState('');
    const [copied, setCopied] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        const generateShareableLink = () => {
            if (!phoneNumber) return;

            try {
                const baseUrl = fanMember
                    ? window.location.origin + fanPath(fanMember.slug, 'member')
                    : window.location.origin + `/${activityType}/${activityId}/member`;
                const url = createShareableUrl(baseUrl, phoneNumber);
                setShareableUrl(url);
            } catch (error) {
                console.error('Error generating shareable link:', error);
            }
        };

        if (phoneNumber) {
            generateShareableLink();
        }
    }, [phoneNumber, activityType, activityId, fanMember]);

    const copyToClipboard = async () => {
        if (!shareableUrl) return;

        try {
            await navigator.clipboard.writeText(shareableUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    if (!phoneNumber) return null;

    return (
        <div className={`rounded-lg overflow-hidden ${theme === 'dark'
            ? 'bg-slate-800/50 border border-slate-700'
            : 'bg-gray-50 border border-gray-200'
            }`}>

            {/* Compact QR Code Header */}
            <div className="px-3 py-2 border-b border-opacity-20 border-gray-400">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <FaQrcode className={`text-sm ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
                        <span className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            Shareable Access
                        </span>
                    </div>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className={`text-xs px-2 py-1 rounded ${theme === 'dark'
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            } transition-colors`}
                    >
                        {isExpanded ? 'Hide' : 'Show'}
                    </button>
                </div>
            </div>

            {/* QR Code Content */}
            {isExpanded && (
                <div className="p-3 space-y-3">
                    {/* QR Code */}
                    <div className="flex justify-center">
                        <div className="bg-white p-2 rounded-lg">
                            <QRCode
                                value={shareableUrl}
                                size={80}
                                level="M"
                                bgColor="white"
                                fgColor="black"
                            />
                        </div>
                    </div>

                    {/* Info Text */}
                    <div className={`text-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        <p className="text-xs">
                            Scan to share access
                        </p>
                        <p className="text-xs opacity-75">
                            Link does not expire
                        </p>
                    </div>

                    {/* Copy Button */}
                    <button
                        onClick={copyToClipboard}
                        className={`w-full py-1.5 px-2 rounded text-xs flex items-center justify-center space-x-1 transition-colors ${theme === 'dark'
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                    >
                        {copied ? (
                            <>
                                <FaCheck className="w-3 h-3" />
                                <span>Copied!</span>
                            </>
                        ) : (
                            <>
                                <FaCopy className="w-3 h-3" />
                                <span>Copy Link</span>
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default MemberCardQR; 